import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { TIER_CONFIG, SubscriptionTier } from "@/lib/subscriptionTiers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, CreditCard, Calendar, FlaskConical, Building2 } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "@/hooks/useTranslation";
import { analytics } from "@/lib/analytics";
import { useCanManageOrg } from "@/lib/auth/roles";
import { ROUTES } from '@/lib/routes';

export function SubscriptionBanner() {
  const { translate } = useTranslation();
  const {
    effectiveTier: tier,
    subscribed,
    isPaid,
    subscriptionEnd,
    loading,
    isBetaOrg,
    startCheckout,
    openCustomerPortal,
  } = useSubscription();

  const navigate = useNavigate();
  const canManageOrg = useCanManageOrg();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  if (loading) {
    return null;
  }

  const tierConfig = (tier && TIER_CONFIG[tier]) ?? TIER_CONFIG.foundation;

  // Tiers the user can upgrade to from their current position
  const upgradeTiers = (Object.keys(TIER_CONFIG) as SubscriptionTier[]).filter(
    (t) => t !== "enterprise" && TIER_CONFIG[t].price > (tierConfig?.price ?? 0)
  );

  const isFoundation = tier === "foundation";
  const isTopTier = tier === "performance_intelligence";

  const handleEnterpriseCta = () => {
    analytics.track("enterprise.cta_clicked", { source: "upgrade_modal" });
    navigate("/enterprise");
  };

  // Beta orgs get a special banner - no billing prompts
  if (isBetaOrg) {
    return (
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <FlaskConical className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{tierConfig.name}</span>
                <Badge variant="outline" className="border-primary/50 text-primary bg-primary/10">
                  <FlaskConical className="h-3 w-3 mr-1" />
                  Beta Access
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">Full feature access — No billing required</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={`${isFoundation ? "border-dashed" : "border-primary/20 bg-primary/5"}`}>
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{tierConfig.name}</span>
                <Badge variant={subscribed ? "default" : "secondary"}>
                  {subscribed ? translate("billing_settings_status_active") : translate("billing_settings_status_free")}
                </Badge>
              </div>
              {subscriptionEnd && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {translate("billing_settings_renews", { date: format(new Date(subscriptionEnd), "dd MMM yyyy") })}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {canManageOrg && isPaid && (
              <Button variant="outline" size="sm" onClick={openCustomerPortal}>
                <CreditCard className="h-4 w-4 mr-2" />
                {translate("billing_settings_btn_manage")}
              </Button>
            )}
            {canManageOrg && (isTopTier ? (
              <Button
                size="sm"
                variant="outline"
                className="border-secondary/40 text-secondary hover:bg-secondary/10 gap-2"
                onClick={handleEnterpriseCta}
              >
                <Building2 className="h-4 w-4" />
                {translate("enterprise_cta_contact_us")}
              </Button>
            ) : (
              upgradeTiers.length > 0 && (
                <Button size="sm" onClick={() => setUpgradeOpen(true)}>
                  <Sparkles className="h-4 w-4 mr-2" />
                  {translate("billing_settings_btn_upgrade")}
                </Button>
              )
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tier selection dialog */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Choose a Plan
            </DialogTitle>
            <DialogDescription>
              Select the plan you'd like to upgrade to.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {upgradeTiers.map((t) => {
              const config = TIER_CONFIG[t];
              return (
                <div
                  key={t}
                  className="flex items-center justify-between p-4 rounded-lg border bg-muted/30"
                >
                  <div>
                    <p className="font-medium">{config.name}</p>
                    <p className="text-sm text-muted-foreground">€{config.price}/mo</p>
                  </div>
                  <Button
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                      analytics.track("subscription.upgrade_clicked", { current_tier: tier, target_tier: t, source: "settings_banner" });
                      setUpgradeOpen(false);
                      startCheckout(t);
                    }}
                  >
                    <Sparkles className="h-3 w-3" />
                    Upgrade
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
